/**
 * Ruling a lead out, from the desk.
 *
 * Three fields, and none of them is the lead's name. The route is addressed by
 * candidate and the service keys the ledger on the name the lead already
 * carries — a form that collected one would let a reviewer file an entry
 * belonging to no lead, which is the state `LeadExclusionCreate` was narrowed
 * to make unstatable.
 *
 * The reason codes come from `/api/taxonomy`. A list hardcoded here would be a
 * second copy of a vocabulary that lives in `domain/taxonomy.py`, in another
 * language, checked by nothing — and it would go stale the first time a reason
 * is added.
 *
 * Nothing here decides whether the draft is acceptable. `rationale` and
 * `reconsiderationTrigger` are refused blank by the service, which answers with
 * a sentence; this renders that sentence rather than restating the rule.
 */
import { useState } from 'react';
import { useExcludeLead } from '../hooks/useDecision';
import { useTaxonomy } from '../hooks/useRadarQuery';
import { label } from '../lib/format';

export function ExcludeForm({ candidateId, nameHint }: { candidateId: string; nameHint: string }) {
  const taxonomy = useTaxonomy();
  const exclude = useExcludeLead(candidateId);
  const [reasonCode, setReasonCode] = useState('');
  const [rationale, setRationale] = useState('');
  const [reconsiderationTrigger, setTrigger] = useState('');

  const reasons = taxonomy.data?.exclusionReasons ?? [];
  return (
    <form
      className="decision-form"
      onSubmit={(submitted) => {
        submitted.preventDefault();
        exclude.mutate({ reasonCode, rationale, reconsiderationTrigger });
      }}
    >
      <label>
        Reason
        <select value={reasonCode} onChange={(changed) => setReasonCode(changed.target.value)}>
          <option value="">Choose a reason…</option>
          {reasons.map((reason) => (
            <option key={reason} value={reason}>{label(reason)}</option>
          ))}
        </select>
      </label>
      <label>
        Why this one
        <input
          type="text"
          value={rationale}
          onChange={(changed) => setRationale(changed.target.value)}
        />
      </label>
      {/* The field that makes this a decision rather than a deletion with a
          record attached, which is how `docs/research-protocol.md` puts it. */}
      <label>
        Reopens if
        <input
          type="text"
          value={reconsiderationTrigger}
          onChange={(changed) => setTrigger(changed.target.value)}
        />
      </label>
      <button className="button" type="submit" disabled={exclude.isPending}>
        Rule out {nameHint}
      </button>
      {exclude.error ? <small className="refusal">{exclude.error.message}</small> : null}
    </form>
  );
}
